import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Box,
  Button,
  Input,
  InputAdornment,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import React, { useState } from 'react';

export const formatCurrency = (amount, currencyCode) => {
  const symbol = getSymbolFromCurrency(currencyCode);
  return `${amount}${symbol}`;
};

export const getCurrencySymbol = (currencyCode) => {
  return getSymbolFromCurrency(currencyCode);
};

// Liste des devises supportées
export const SUPPORTED_CURRENCIES = {
  EUR: {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
  },
  USD: {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
  },
  GBP: {
    code: 'GBP',
    name: 'British Pound',
    symbol: '£',
  },
  // Ajoutez d'autres devises selon vos besoins
};

const FeesCalculations = ({
  countryZone,
  productsAmount,
  currencyCode,
  calculMethod,
  setCalculMethod,
  retractFeeAmount,
  retractFeeAmountEuros,
  setRetractFeeAmount,
  refundAmount,
  setRefundAmount,
  partialRefundAmount,
  carrierOption,
  store,
  setForcePackageFees,
  forcePackageFees,
}) => {
  const [errorText, setErrorText] = useState(null);
  const handleRetractFeeAmountChange = (e) => {
    const value = e.target.value;
    const numberFormat = /^\d*\.?\d{0,2}$/;
    if (numberFormat.test(value) || value === '') {
      if (productsAmount - value < 0) {
        setErrorText(
          'Les frais de rétractation ne peuvent être supérieurs au montant de la commande'
        );
        return;
      } else {
        setErrorText(null);
      }

      setRetractFeeAmount(value);
      setRefundAmount((productsAmount - value).toFixed(2));
    }
  };

  const countryZoneDetails = countryZone?.countries
    .map((c) => c.name)
    .join(', ');

  const isLeroyMerlin = store?.brand?.name === 'LeroyMerlin';

  console.log('isLeroyMerlin', isLeroyMerlin);

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Typography
        color="primary"
        sx={{ fontSize: '1em', fontWeight: 'bold', mb: 1 }}
      >
        Calcul des frais{' '}
        <Tooltip
          placement="top"
          title={
            `Le délai légal de rétractation pour ${store?.name} est de ${store?.legalRetractTime} jours. ${store?.hasRetractFees ? 'Les frais de rétractation sont applicables.' : ' Les frais de rétractation ne sont pas applicables dans ce délai.'}` +
            (isLeroyMerlin
              ? ' Les frais applicables sont plafonés (par colis) pour cette MarketPlace.'
              : '')
          }
        >
          <InfoOutlineIcon sx={{ fontSize: '1em', cursor: 'pointer' }} />
        </Tooltip>
      </Typography>
      {forcePackageFees && (
        <Typography
          sx={{
            fontSize: '0.8em',
            color: 'error.main',
            mb: 1,
            backgroundColor: 'error.light',
            padding: '0.5em 1em',
            borderRadius: '4px',
          }}
        >
          Vérifiez l&apos;application des frais de rétractation. Les CGV de{' '}
          {store?.name} ne prévoient pas l&apos;application frais de
          rétractation.
        </Typography>
      )}
      <Box
        sx={{
          mb: 3,
          alignItems: 'center',
          justifyContent: 'space-between',
          display: 'flex',
          gap: 1,
          flexDirection: 'row',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            disabled={!store?.hasRetractFees && !forcePackageFees}
            onClick={() => setCalculMethod('total')}
            variant={calculMethod === 'total' ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '0.7em', padding: '0 0.5em' }}
          >
            Frais de rétractation
          </Button>
          <Tooltip
            placement="top"
            title={`Modifiez le montant dans le champ "Frais à appliquer"`}
          >
            <Button
              disabled={!store?.hasRetractFees && !forcePackageFees}
              onClick={() => setCalculMethod('free')}
              variant={calculMethod === 'free' ? 'contained' : 'outlined'}
              size="small"
              sx={{ fontSize: '0.7em', padding: '0 0.5em' }}
            >
              Montant libre
            </Button>
          </Tooltip>
          <Button
            onClick={() => setCalculMethod('withoutFees')}
            variant={calculMethod === 'withoutFees' ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '0.7em', padding: '0 0.5em' }}
          >
            Retour Gratuit
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!store?.hasRetractFees && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1em', cursor: 'pointer' }}>
                Éditer le montant{' '}
              </Typography>
              <Switch
                checked={forcePackageFees}
                onChange={() => setForcePackageFees(!forcePackageFees)}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3,
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            padding: '0.5em 0',
            gap: 1,
            mb: 3,
            width: '30%',
          }}
        >
          <div
            style={{
              width: '100%',
              border: '1px solid black',
              padding: '0.5em 1em',
              borderRadius: '10px',
            }}
          >
            <Typography
              sx={{ fontSize: '0.8em', color: 'primary.main', mb: 1 }}
            >
              Zone du Pays
            </Typography>
            <Tooltip title={countryZoneDetails}>
              <Typography sx={{ fontSize: '0.9em' }}>
                {countryZone?.name}
              </Typography>
            </Tooltip>
          </div>
        </Box>
        <Box
          sx={[
            {
              display: 'flex',
              padding: '0.5em 1em',
              gap: 1,
              mb: 3,
              width: '70%',
              backgroundColor: 'rgba(0, 0, 255, 0.05)',
            },
          ]}
        >
          <div
            style={{
              width: '50%',
              border: '1px solid black',
              padding: '0.5em 1em',
              borderRadius: '10px',
              backgroundColor: 'white',
              position: 'relative',
            }}
          >
            <span
              style={{
                fontSize: '0.8em',
                color: 'red',
                position: 'absolute',
                bottom: '-3.5em',
                left: 0,
              }}
            >
              {errorText}
            </span>

            <Typography
              sx={{ fontSize: '0.8em', color: 'primary.main', mb: 1 }}
            >
              Frais à appliquer
            </Typography>
            {calculMethod === 'free' && (
              <Input
                value={retractFeeAmount}
                error={Boolean(errorText)}
                endAdornment={
                  <InputAdornment position="end">
                    {getCurrencySymbol(currencyCode)}
                  </InputAdornment>
                }
                onChange={handleRetractFeeAmountChange}
              />
            )}
            {calculMethod !== 'free' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: '0.9em' }}>
                  {`${retractFeeAmount} ${getCurrencySymbol(currencyCode)}`}{' '}
                </Typography>
                <Typography sx={{ fontSize: '0.75em' }}>
                  {currencyCode !== 'EUR' && `(${retractFeeAmountEuros} €)`}
                </Typography>
              </Box>
            )}
          </div>
          <div
            style={{
              width: '50%',
              border: '1px solid black',
              padding: '0.5em 1em',
              borderRadius: '10px',
              backgroundColor: 'white',
            }}
          >
            <Typography sx={{ fontSize: '0.8em', color: 'primary.main' }}>
              Remboursement à effectuer
            </Typography>
            <Typography sx={{ fontSize: '0.9em' }}>
              {refundAmount} {getCurrencySymbol(currencyCode)}
            </Typography>
          </div>
        </Box>
      </Box>
      <Typography sx={{ mb: 1, fontSize: '0.9em' }}>
        Si le client passe une seconde commande :
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <div
          style={{
            width: '50%',
            border: '1px solid black',
            padding: '0.5em 1em',
            borderRadius: '10px',
            marginBottom: '2em',
          }}
        >
          <Typography sx={{ fontSize: '0.8em', color: 'primary.main', mb: 1 }}>
            Remboursement à effectuer
          </Typography>
          <Typography sx={{ fontSize: '0.9em' }}>
            {partialRefundAmount} {getCurrencySymbol(currencyCode)}
          </Typography>
        </div>
        <div
          style={{
            width: '50%',
            border: '1px solid black',
            padding: '0.5em 1em',
            borderRadius: '10px',
            marginBottom: '2em',
          }}
        >
          <Typography sx={{ fontSize: '0.8em', color: 'primary.main', mb: 1 }}>
            Option de transport disponible
          </Typography>
          <Typography sx={{ fontSize: '0.9em' }}>{carrierOption}</Typography>
        </div>
      </Box>
    </Box>
  );
};

export default FeesCalculations;
